import React, { useCallback, useEffect, useState } from "react";
import { useAdminApi } from "./useAdminApi";
import { REGION_LABELS } from "./regionLabels";
import ImageLightbox from "./ImageLightbox";

const PAGE_SIZE = 20;
const LINKED_FILTERS = [
  { key: "", label: "All" },
  { key: "linked", label: "Linked" },
  { key: "unlinked", label: "Unlinked" },
];

function LinkToIgdbForm({ entryId, onLink }) {
  const [igdbId, setIgdbId] = useState("");
  const [platformId, setPlatformId] = useState("");
  const [linking, setLinking] = useState(false);
  const [error, setError] = useState(null);

  async function handleSubmit(e) {
    e.preventDefault();
    const parsedIgdbId = Number(igdbId);
    const parsedPlatformId = Number(platformId);
    if (!Number.isInteger(parsedIgdbId) || parsedIgdbId <= 0 || !Number.isInteger(parsedPlatformId) || parsedPlatformId <= 0) return;

    setLinking(true);
    setError(null);
    try {
      await onLink(entryId, parsedIgdbId, parsedPlatformId);
      setIgdbId("");
      setPlatformId("");
    } catch (err) {
      setError(err.message === "Forbidden" ? null : "That igdb_id/platform_id isn't tracked yet");
    } finally {
      setLinking(false);
    }
  }

  return (
    <form className="admin-tgdb-link-form" onSubmit={handleSubmit}>
      <input
        type="number"
        min="1"
        placeholder="igdb_id"
        value={igdbId}
        onChange={(e) => setIgdbId(e.target.value)}
        className="admin-tgdb-link-input"
      />
      <input
        type="number"
        min="1"
        placeholder="platform_id"
        value={platformId}
        onChange={(e) => setPlatformId(e.target.value)}
        className="admin-tgdb-link-input"
      />
      <button type="submit" className="admin-signout-btn" disabled={linking || !igdbId || !platformId}>
        {linking ? "Linking…" : "Link"}
      </button>
      {error && <div className="admin-status admin-error admin-status--inline">{error}</div>}
    </form>
  );
}

function CoversTab({ user, onForbidden }) {
  const apiFetch = useAdminApi(user, onForbidden);
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [nameInput, setNameInput] = useState("");
  const [name, setName] = useState("");
  const [linkedFilter, setLinkedFilter] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    const handle = setTimeout(() => {
      setName(nameInput.trim());
      setPage(1);
    }, 350);
    return () => clearTimeout(handle);
  }, [nameInput]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ page: String(page), page_size: String(PAGE_SIZE) });
      if (name) params.set("name", name);
      if (linkedFilter) params.set("linked", linkedFilter);
      const data = await apiFetch(`/admin/thegamesdb-games?${params.toString()}`);
      setItems(data.items);
      setTotalPages(data.total_pages || 1);
      setTotal(data.total || 0);
    } catch (err) {
      if (err.message !== "Forbidden") setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [apiFetch, page, name, linkedFilter]);

  useEffect(() => {
    load();
  }, [load]);

  async function linkToIgdb(entryId, igdbId, platformId) {
    await apiFetch(`/admin/thegamesdb-games/${entryId}/link-to-igdb`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ igdb_id: igdbId, platform_id: platformId }),
    });
    load();
  }

  function selectLinkedFilter(key) {
    setLinkedFilter(key);
    setPage(1);
  }

  return (
    <div>
      <p className="kicker">Curated source</p>
      <h2 className="admin-title admin-title--left">Covers</h2>

      <div className="admin-links-search">
        <input
          type="text"
          placeholder="Search by name…"
          value={nameInput}
          onChange={(e) => setNameInput(e.target.value)}
          className="admin-tgdb-link-input admin-links-search-input"
        />
        <div className="admin-linked-filter">
          {LINKED_FILTERS.map((f) => (
            <button
              key={f.key || "all"}
              type="button"
              className={`admin-tab admin-linked-filter-btn ${linkedFilter === f.key ? "admin-tab-active" : ""}`}
              onClick={() => selectLinkedFilter(f.key)}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {loading && <p className="admin-status">Loading…</p>}
      {error && <p className="admin-status admin-error">{error}</p>}

      <div className="admin-table glass">
        {items && items.length === 0 && <p className="admin-status">No covers found.</p>}
        {items && items.map((item) => (
          <div className="admin-row admin-row--covers" key={item._id}>
            <div className="admin-boxart-sides admin-boxart-sides--row">
              {item.boxart_url
                ? (
                  <img
                    className="admin-boxart admin-boxart--row admin-boxart-clickable"
                    src={item.boxart_url}
                    alt={`${item.name} front`}
                    onClick={() => setLightbox({ src: item.boxart_url, alt: `${item.name} front` })}
                  />
                )
                : <div className="admin-boxart-placeholder admin-boxart-placeholder--row">No art</div>}
              <div className="admin-boxart-back-slot">
                {item.back_boxart_url && (
                  <img
                    className="admin-boxart admin-boxart--row admin-boxart-clickable"
                    src={item.back_boxart_url}
                    alt={`${item.name} back`}
                    title="Back cover"
                    onClick={() => setLightbox({ src: item.back_boxart_url, alt: `${item.name} back` })}
                  />
                )}
              </div>
            </div>
            <div className="admin-row-main">
              <div className="admin-source-name">{item.name}</div>
              <div className="admin-source-meta">
                platform_id {item.platform_id} · {REGION_LABELS[item.region_id] || "Unknown region"} · tgdb_id {item.tgdb_id}
              </div>
              {item.linked ? (
                <div className="admin-status--inline admin-covers-linked">Linked to {item.linked_to || "a game"}</div>
              ) : (
                <LinkToIgdbForm entryId={item._id} onLink={linkToIgdb} />
              )}
            </div>
          </div>
        ))}
      </div>

      {items && items.length > 0 && (
        <div className="admin-pagination">
          <button
            type="button"
            className="admin-signout-btn"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1 || loading}
          >
            Previous
          </button>
          <span className="admin-pagination-status">
            Page {page} of {totalPages} · {total} total
          </span>
          <button
            type="button"
            className="admin-signout-btn"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages || loading}
          >
            Next
          </button>
        </div>
      )}

      <ImageLightbox src={lightbox?.src} alt={lightbox?.alt} onClose={() => setLightbox(null)} />
    </div>
  );
}

export default CoversTab;
